<template>
  <b-overlay
    :show="show"
    spinner-variant="primary"
    spinner-type="grow"
    spinner-small
    rounded="sm"
  >
    <b-row>
      <b-col
        cols="12"
        lg="8"
      >
        <b-card>
          <div class="d-flex flex-wrap align-items-start justify-content-between">
            <div class="mb-1">
              <h3 class="mb-50">
                {{ $t('pamm_account_title', { name: accountName }) }}
              </h3>
              <p class="text-muted mb-0">
                {{ $t('pamm_account_description2') }}
              </p>
            </div>
            <b-button
              :href="`${accountDetailsUrl}/${$route.params.id}`"
              target="_blank"
              variant="outline-primary"
              size="sm"
              class="mb-1 text-nowrap"
            >
              <feather-icon
                icon="ExternalLinkIcon"
                size="14"
              />
              <span class="mx-50">{{ $t('details_show') }}</span>
            </b-button>
          </div>
        </b-card>

        <b-card v-if="loaded">
          <div class="d-flex align-items-center justify-content-between mb-1">
            <h4 class="card-title mb-0">
              {{ $t('pamm_current_period') }}
            </h4>
            <b-badge
              v-if="period"
              :variant="periodStatusVariants[period.status] || 'light-secondary'"
            >
              {{ $te(`pamm_period_status_${period.status}`) ? $t(`pamm_period_status_${period.status}`) : period.status }}
            </b-badge>
          </div>
          <b-row v-if="period">
            <b-col
              v-for="window in periodWindows"
              :key="window.label"
              cols="12"
              sm="6"
              class="mb-1"
            >
              <div class="pamm-term h-100">
                <div class="d-flex align-items-center text-muted mb-50">
                  <feather-icon
                    :icon="window.icon"
                    size="15"
                  />
                  <span class="mx-50">{{ $t(window.label) }}</span>
                </div>
                <div class="font-weight-bolder text-nowrap">
                  {{ formatDate(period[window.start]) }}
                </div>
                <div class="font-weight-bolder text-nowrap">
                  {{ formatDate(period[window.end]) }}
                </div>
              </div>
            </b-col>
          </b-row>
          <p
            v-else
            class="text-muted mb-0"
          >
            {{ $t('pamm_no_period') }}
          </p>
        </b-card>

        <b-card :title="$t('pamm_account_description1')">
          <div
            v-for="group in termGroups"
            :key="group.title"
            class="mb-1"
          >
            <h6 class="text-muted mb-1">
              {{ $t(group.title) }}
            </h6>
            <b-row>
              <b-col
                v-for="term in group.terms"
                :key="term.key"
                cols="12"
                sm="6"
                class="mb-1"
              >
                <div class="pamm-term h-100">
                  <div class="d-flex align-items-center text-muted">
                    <span>{{ $t(term.label) }}</span>
                    <feather-icon
                      :id="`term-${term.key}`"
                      icon="HelpCircleIcon"
                      size="15"
                      class="mx-50 cursor-pointer"
                      tabindex="0"
                    />
                    <b-popover
                      :target="`term-${term.key}`"
                      triggers="hover focus"
                      placement="top"
                    >
                      {{ $t(`${term.label}_description`) }}
                    </b-popover>
                  </div>
                  <h4 class="font-weight-bolder mb-0 mt-50">
                    {{ formatValue(termValue(term), term.unit) }}
                  </h4>
                </div>
              </b-col>
            </b-row>
          </div>
        </b-card>
      </b-col>

      <b-col
        cols="12"
        lg="4"
      >
        <b-card
          :title="$t('pamm_invest_form_title')"
          class="pamm-invest-card"
        >
          <b-alert
            v-if="!accountsPending && platformAccounts.length === 0"
            show
            variant="warning"
            class="p-1"
          >
            <p class="mb-1">
              {{ $t('pamm_no_trading_account') }}
            </p>
            <b-button
              :to="{ name: 'forexModule-metatrader-create-account' }"
              variant="warning"
              size="sm"
            >
              {{ $t('pamm_create_trading_account') }}
            </b-button>
          </b-alert>

          <b-alert
            v-if="loaded && !canInvest"
            show
            variant="danger"
            class="p-1"
          >
            {{ period ? $t('pamm_period_closed') : $t('pamm_no_period') }}
          </b-alert>

          <validation-observer v-slot="{ invalid }">
            <b-form @submit.prevent>
              <b-form-group
                :label="$t('account')"
                label-for="platform_account_id"
              >
                <validation-provider
                  v-slot="{ errors }"
                  :name="$t('account')"
                  rules="required"
                >
                  <v-select
                    id="platform_account_id"
                    v-model="invest.platform_account_id"
                    :options="platformAccounts"
                    :reduce="item => item.account"
                    :clearable="false"
                    :loading="accountsPending"
                    label="account"
                    :placeholder="$t('account')"
                  >
                    <template #option="option">
                      <div class="d-flex justify-content-between">
                        <span>{{ option.account }}</span>
                        <small class="text-muted">{{ formatValue(option.balance, '$') }}</small>
                      </div>
                    </template>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
                <small
                  v-if="selectedPlatformAccount"
                  class="text-muted d-block mt-50"
                >
                  {{ $t('balance') }}: {{ formatValue(selectedPlatformAccount.balance, '$') }}
                </small>
              </b-form-group>

              <b-form-group
                :label="$t('amount')"
                label-for="amount"
              >
                <validation-provider
                  v-slot="{ errors }"
                  :name="$t('amount')"
                  :rules="amountRules"
                >
                  <b-input-group append="$">
                    <b-form-input
                      id="amount"
                      v-model.number="invest.amount"
                      type="number"
                      step="any"
                      :min="account.minimum_invest_amount"
                      :max="account.maximum_invest_amount"
                      :state="errors.length > 0 || exceedsBalance ? false : null"
                      :placeholder="$t('amount')"
                      autocomplete="nope"
                    />
                  </b-input-group>
                  <small class="text-danger d-block">{{ errors[0] }}</small>
                </validation-provider>
                <small
                  v-if="exceedsBalance"
                  class="text-danger d-block"
                >
                  {{ $t('pamm_amount_exceeds_balance') }}
                </small>
                <div
                  v-if="hasLimits"
                  class="d-flex align-items-center justify-content-between flex-wrap mt-50"
                >
                  <small class="text-muted">
                    {{ $t('pamm_invest_range', { min: formatValue(account.minimum_invest_amount, '$'), max: formatValue(account.maximum_invest_amount, '$') }) }}
                  </small>
                  <div class="text-nowrap">
                    <b-button
                      variant="flat-primary"
                      size="sm"
                      class="px-50 py-25"
                      @click="invest.amount = account.minimum_invest_amount"
                    >
                      {{ $t('pamm_minimum') }}
                    </b-button>
                    <b-button
                      variant="flat-primary"
                      size="sm"
                      class="px-50 py-25"
                      @click="invest.amount = maxAllowedAmount"
                    >
                      {{ $t('pamm_maximum') }}
                    </b-button>
                  </div>
                </div>
              </b-form-group>

              <b-form-group class="mt-2">
                <b-form-checkbox
                  id="isAgree"
                  v-model="isAgree"
                  name="isAgree"
                >
                  <small>{{ $t('pamm_invest_conditions') }}</small>
                </b-form-checkbox>
              </b-form-group>

              <b-button
                :disabled="!canInvest || !isAgree || invalid || exceedsBalance || investPending"
                block
                variant="success"
                @click="confirmModalShow = true"
              >
                {{ $t('start_investing') }}
              </b-button>
            </b-form>
          </validation-observer>
        </b-card>
      </b-col>
    </b-row>

    <b-modal
      v-model="confirmModalShow"
      centered
      :title="$t('pamm_confirm_invest_title')"
      :no-close-on-backdrop="investPending"
      :no-close-on-esc="investPending"
      :hide-header-close="investPending"
    >
      <dl class="row mb-0">
        <dt class="col-6 font-weight-normal text-muted">
          {{ $t('pamm_manager') }}
        </dt>
        <dd class="col-6 font-weight-bold">
          {{ accountName }}
        </dd>
        <dt class="col-6 font-weight-normal text-muted">
          {{ $t('account') }}
        </dt>
        <dd class="col-6 font-weight-bold">
          {{ selectedPlatformAccount ? selectedPlatformAccount.account : '-' }}
        </dd>
        <dt class="col-6 font-weight-normal text-muted">
          {{ $t('amount') }}
        </dt>
        <dd class="col-6 font-weight-bold">
          {{ formatValue(invest.amount, '$') }}
        </dd>
      </dl>
      <b-alert
        show
        variant="warning"
        class="p-1 mt-1 mb-0"
      >
        {{ $t('pamm_confirm_invest_description', { days: formatValue(period && period.premature_cancelation_period, ''), fine: formatValue(period && period.premature_cancelation_fine_percent, '') }) }}
      </b-alert>
      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          :disabled="investPending"
          @click="confirmModalShow = false"
        >
          {{ $t('cancel') }}
        </b-button>
        <b-button
          variant="success"
          :disabled="investPending"
          @click="submitInvest"
        >
          <b-spinner
            v-if="investPending"
            small
            type="grow"
          />
          <span v-else>{{ $t('confirm') }}</span>
        </b-button>
      </template>
    </b-modal>
  </b-overlay>
</template>

<script>
import {
  BOverlay,
  BCard,
  BRow,
  BCol,
  BButton,
  BForm,
  BFormGroup,
  BFormCheckbox,
  BFormInput,
  BInputGroup,
  BSpinner,
  BPopover,
  BAlert,
  BModal,
  BBadge,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import jalaali from 'moment-jalaali'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, between } from '@validations'
import PammApis from '@/modules/pamm/service/apis'
import MetatraderService from '@/modules/forex/service/apis'
import { numberWithCommas } from '@/utils/index'

const pammApis = new PammApis()
const metatraderService = new MetatraderService()

export default {
  components: {
    BOverlay,
    BCard,
    BRow,
    BCol,
    BButton,
    BForm,
    BFormGroup,
    BFormCheckbox,
    BFormInput,
    BInputGroup,
    BSpinner,
    BPopover,
    BAlert,
    BModal,
    BBadge,
    vSelect,
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      accountDetailsUrl: process.env.VUE_APP_PAMM_ACCOUNT_BASE_URL,
      show: false,
      isAgree: false,
      investPending: false,
      accountsPending: false,
      confirmModalShow: false,
      required,
      between,
      platformAccounts: [],
      invest: {
        platform_account_id: null,
        amount: null,
      },
      account: {},
      loaded: false,
      periodStatusVariants: {
        subscribing: 'light-primary',
        freezing: 'light-warning',
        trading: 'light-success',
        end: 'light-secondary',
      },
      periodWindows: [
        {
          label: 'pamm_invest_window', icon: 'LogInIcon', start: 'invest_start_at', end: 'invest_end_at',
        },
        {
          label: 'pamm_trade_window', icon: 'TrendingUpIcon', start: 'trade_start_at', end: 'trade_end_at',
        },
      ],
    }
  },
  computed: {
    period() {
      return this.account.latest_period ?? null
    },
    canInvest() {
      return this.period?.is_open_for_subscription === true
    },
    accountName() {
      return this.$route.query.name || `#${this.$route.params.id}`
    },
    hasLimits() {
      return this.account.minimum_invest_amount !== undefined && this.account.maximum_invest_amount !== undefined
    },
    amountRules() {
      if (!this.hasLimits) return 'required'
      return `required|between:${this.account.minimum_invest_amount},${this.account.maximum_invest_amount}`
    },
    selectedPlatformAccount() {
      return this.platformAccounts.find(item => item.account === this.invest.platform_account_id) ?? null
    },
    selectedBalance() {
      const balance = Number(this.selectedPlatformAccount?.balance)
      return this.selectedPlatformAccount && Number.isFinite(balance) ? balance : null
    },
    exceedsBalance() {
      return this.selectedBalance !== null && Number(this.invest.amount) > this.selectedBalance
    },
    maxAllowedAmount() {
      const maxAmount = this.account.maximum_invest_amount
      return this.selectedBalance === null ? maxAmount : Math.min(maxAmount, this.selectedBalance)
    },
    termGroups() {
      return [
        {
          title: 'pamm_terms_fees',
          terms: [
            { key: 'fee_static', label: 'fee_static', unit: '$' },
            { key: 'fee_percent', label: 'fee_percent', unit: '%' },
          ],
        },
        {
          title: 'pamm_terms_sharing',
          terms: [
            { key: 'profit_share_percent', label: 'profit_share', unit: '%' },
            { key: 'loss_share_percent', label: 'loss_share', unit: '%' },
          ],
        },
        {
          title: 'pamm_terms_cancelation',
          terms: [
            { key: 'premature_cancelation_period', label: 'caneclation_period', unit: this.$t('day') },
            { key: 'premature_cancelation_fine_percent', label: 'caneclation_fee', unit: '%' },
          ],
        },
        {
          title: 'pamm_terms_limits',
          terms: [
            {
              key: 'minimum_invest_amount', label: 'minimum_investment', unit: '$', fromManager: true,
            },
            {
              key: 'maximum_invest_amount', label: 'maximum_investment', unit: '$', fromManager: true,
            },
          ],
        },
      ]
    },
  },
  mounted() {
    this.getAccountInfo()
    this.getPlatformAccounts()
  },
  methods: {
    async getAccountInfo() {
      this.show = true
      await pammApis.getInvestorManagerDetails(this.$route.params.id).then(res => {
        this.account = res.data.results ?? {}
      }).catch(err => {
        console.log(err)
      }).finally(() => {
        this.show = false
        this.loaded = true
      })
    },
    async getPlatformAccounts() {
      this.accountsPending = true
      await metatraderService.getAccounts().then(res => {
        this.platformAccounts = res.data.results ?? []
        if (this.platformAccounts.length === 1) {
          this.invest.platform_account_id = this.platformAccounts[0].account
        }
      }).catch(err => {
        console.log(err)
      }).finally(() => {
        this.accountsPending = false
      })
    },
    termValue(term) {
      return term.fromManager ? this.account[term.key] : this.period?.[term.key]
    },
    formatDate(date) {
      if (!date) return '-'
      return jalaali(date).format(this.$i18n.locale === 'fa' ? 'jYYYY/jMM/jDD HH:mm' : 'YYYY-MM-DD HH:mm')
    },
    formatValue(value, unit) {
      if (value === null || value === undefined) return '-'
      return `${numberWithCommas(value)} ${unit}`.trim()
    },
    async submitInvest() {
      this.investPending = true
      const data = {
        ...this.invest,
        platform: this.selectedPlatformAccount?.platform,
        platform_account: this.invest.platform_account_id,
      }
      await pammApis.followManager(this.$route.params.id, data).then(res => {
        this.confirmModalShow = false
        this.$swal({
          title: res.data.message,
          icon: 'success',
          timer: 4000,
          showConfirmButton: false,
        })
        this.$router.push({ name: 'pamm_own_invest' })
      }).catch(err => {
        console.log(err)
      }).finally(() => {
        this.investPending = false
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.pamm-term {
  padding: 1rem;
  border: 1px solid rgba(34, 41, 47, 0.1);
  border-radius: 0.428rem;
}

.dark-layout .pamm-term {
  border-color: #3b4253;
}

@media (min-width: 992px) {
  .pamm-invest-card {
    position: sticky;
    top: 6rem;
  }
}
</style>
